/**
 * <newsletter-popup>
 *
 * A welcome-offer popup built on a native modal <dialog> (focus trap, Escape and inert background for free).
 *
 * - Opens after `data-delay` seconds.
 * - Remembers a dismissal for `data-days` days, and a subscription for a year, in localStorage.
 * - Submits the Shopify customer form with fetch so the page never reloads, then shows the welcome code.
 * - If the browser blocks fetch or Shopify asks for a captcha, it falls back to a normal form submit.
 * - In the theme editor it opens while its section is selected and never touches storage.
 */
class NewsletterPopup extends HTMLElement {
  static STORAGE_KEY = 'aroma-newsletter-popup';
  static CLOSE_MS = 450;

  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.card = this.querySelector('.np__card');
    this.form = this.querySelector('form');
    this.emailInput = this.querySelector('input[type="email"]');
    this.submitButton = this.querySelector('[data-np-submit]');
    this.errorEl = this.querySelector('[data-np-error]');
    this.copyButton = this.querySelector('[data-np-copy]');
    if (!this.dialog || !this.form) return;

    this.delay = Math.max(0, Number(this.dataset.delay || 5)) * 1000;
    this.days = Math.max(1, Number(this.dataset.days || 14));
    this.designMode = Boolean(window.Shopify && window.Shopify.designMode);
    this.sectionId = this.dataset.sectionId;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.querySelectorAll('[data-np-close]').forEach((el) => el.addEventListener('click', () => this.dismiss()));
    this.dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.dismiss();
    });
    // Clicks on the dimmed area land on the <dialog> itself, not on the card.
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.dismiss();
    });
    this.form.addEventListener('submit', (event) => this.onSubmit(event));
    this.copyButton?.addEventListener('click', () => this.copyCode());

    if (this.designMode) {
      this.onSelect = (event) => {
        if (event.detail.sectionId === this.sectionId) this.open();
      };
      this.onDeselect = (event) => {
        if (event.detail.sectionId === this.sectionId) this.close();
      };
      document.addEventListener('shopify:section:select', this.onSelect);
      document.addEventListener('shopify:section:deselect', this.onDeselect);
      return;
    }

    // Returned from a non-JS submit: show the result straight away.
    if (this.querySelector('[data-np-posted]')) {
      this.showSuccess();
      this.open();
      return;
    }
    if (this.querySelector('[data-np-has-errors]')) {
      this.open();
      return;
    }

    if (this.isSuppressed()) return;
    this.timer = window.setTimeout(() => this.open(), this.delay);
  }

  disconnectedCallback() {
    window.clearTimeout(this.timer);
    if (this.onSelect) document.removeEventListener('shopify:section:select', this.onSelect);
    if (this.onDeselect) document.removeEventListener('shopify:section:deselect', this.onDeselect);
    this.unlockScroll();
  }

  /* ---------- state remembered between visits ---------- */

  readState() {
    try {
      return JSON.parse(window.localStorage.getItem(NewsletterPopup.STORAGE_KEY)) || {};
    } catch (e) {
      return {};
    }
  }

  writeState(state) {
    try {
      window.localStorage.setItem(NewsletterPopup.STORAGE_KEY, JSON.stringify(state));
    } catch (e) {
      /* storage unavailable (private mode): the popup may show again, which is acceptable */
    }
  }

  isSuppressed() {
    const state = this.readState();
    if (state.subscribedAt) return true;
    if (state.dismissedAt) {
      const ageDays = (Date.now() - state.dismissedAt) / 86400000;
      return ageDays < this.days;
    }
    return false;
  }

  /* ---------- open and close ---------- */

  open() {
    if (this.dialog.open) return;
    this.returnFocusTo = document.activeElement;
    this.dialog.showModal();
    this.lockScroll();
    // Two frames so the starting styles paint before the transition begins.
    window.requestAnimationFrame(() => window.requestAnimationFrame(() => this.setAttribute('data-visible', '')));
    this.card?.focus({ preventScroll: true });
  }

  close() {
    if (!this.dialog.open) return;
    this.removeAttribute('data-visible');
    const finish = () => {
      if (this.dialog.open) this.dialog.close();
      this.unlockScroll();
      if (this.returnFocusTo && typeof this.returnFocusTo.focus === 'function') this.returnFocusTo.focus({ preventScroll: true });
    };
    if (this.reducedMotion) finish();
    else window.setTimeout(finish, NewsletterPopup.CLOSE_MS);
  }

  dismiss() {
    if (!this.designMode && !this.subscribed) {
      this.writeState({ ...this.readState(), dismissedAt: Date.now() });
    }
    this.close();
  }

  lockScroll() {
    this.previousOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
  }

  unlockScroll() {
    if (this.previousOverflow !== undefined) {
      document.documentElement.style.overflow = this.previousOverflow;
      this.previousOverflow = undefined;
    }
  }

  /* ---------- subscribing ---------- */

  showError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message;
    this.errorEl.hidden = !message;
    this.emailInput?.setAttribute('aria-invalid', message ? 'true' : 'false');
  }

  setLoading(loading) {
    if (!this.submitButton) return;
    this.submitButton.disabled = loading;
    this.submitButton.setAttribute('aria-busy', String(loading));
    this.submitButton.textContent = loading ? this.dataset.loadingLabel : this.dataset.buttonLabel;
  }

  showSuccess() {
    this.subscribed = true;
    this.setAttribute('data-state', 'success');
    if (!this.designMode) this.writeState({ ...this.readState(), subscribedAt: Date.now() });
    this.querySelector('[data-np-success-heading]')?.focus({ preventScroll: true });
  }

  async onSubmit(event) {
    event.preventDefault();
    this.showError('');

    const email = (this.emailInput?.value || '').trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
      this.showError(this.dataset.invalidMessage);
      this.emailInput?.focus();
      return;
    }

    this.setLoading(true);
    try {
      const response = await fetch(this.form.action, {
        method: 'POST',
        body: new FormData(this.form),
        headers: { Accept: 'text/html' },
        credentials: 'same-origin',
      });

      if (response.url.includes('/challenge')) {
        // Shopify wants a captcha: hand over to a regular form submit.
        this.form.submit();
        return;
      }
      if (response.ok && response.url.includes('customer_posted=true')) {
        this.setLoading(false);
        this.showSuccess();
        return;
      }
      throw new Error('Unexpected response');
    } catch (error) {
      this.setLoading(false);
      this.showError(this.dataset.errorMessage);
    }
  }

  async copyCode() {
    const code = this.dataset.code;
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      this.copyButton.textContent = this.dataset.copiedLabel;
      window.setTimeout(() => {
        this.copyButton.textContent = this.dataset.copyLabel;
      }, 2000);
    } catch (e) {
      /* clipboard blocked: the code is still visible to copy by hand */
    }
  }
}

if (!customElements.get('newsletter-popup')) {
  customElements.define('newsletter-popup', NewsletterPopup);
}
